// Stands in for the product specification the backend does not model yet: print
// techniques, marking zones, cup colours, carton size, family. The socle's Product only
// carries one width and one height, so everything below is simulated from the client's
// technical sheets (Caractéristiques techniques v1.3 and the WANADEV spreadsheet,
// September 2026) and from the public product pages of gobeletcup.fr.
//
// Prototype data, CDC domain CFG. To be replaced by real product data once Timotée and
// Léo decide how marking zones are modelled. Tracked in PROTO-NOTES.md.

type TCatalogFamily = 'cocktail' | 'cup' | 'pitcher' | 'wine';

type TCupColour = 'black' | 'blue' | 'fuchsia' | 'green' | 'ice' | 'orange' | 'red' | 'white' | 'yellow';

// A printable rectangle on the unrolled surface, in millimetres. Margins are the
// non-printable bands around it, measured from the edge of the total surface.
interface IMarkingZone {
    height: number;
    marginBottom: number;
    marginTop: number;
    width: number;
}

// The client offers three vertical placements for the short quadri zone on the
// tallest cups. Only `centre` exists everywhere the zone exists at all.
type TQuadriPlacement = 'bottom' | 'centre' | 'top';

interface ICatalogReference {
    cartonSize: number;
    colours: TCupColour[];
    family: TCatalogFamily;
    hasIml: boolean;
    /** Printed on two opposite faces when the customer asks for it: the two pitchers. */
    hasVerso?: boolean;
    imageUrl: string;
    /** Marketing capacity as the site prints it, e.g. "25/33 cl". */
    label: string;
    name: string;
    /** Short quadri (digital) zone, when the reference allows it. */
    quadriShort:
        | (IMarkingZone & { placements: Partial<Record<TQuadriPlacement, Pick<IMarkingZone, 'marginBottom' | 'marginTop'>>> })
        | null;
    /**
     * Full-height quadri zone (one option for the customer, "Quadri grande hauteur"). Two zones behind
     * it, told apart in the back-office only: `digital`, with a margin above and below, for a small
     * run or the Express option, and `iml`, the whole printable height, from a larger run (Willy's
     * table of 2026-10-26). The three smallest references have the `iml` zone alone.
     */
    quadriTall: { digital: IMarkingZone | null; iml: IMarkingZone } | null;
    /** Screen printing zone (one or two colours). Side margin is symmetric. */
    serigraphy: IMarkingZone & { marginSide: number; maxColours: 1 | 2 };
    /** Total unrolled surface of the cup, width x height in mm. */
    surface: { height: number; width: number };
}

const ALL_CUP_COLOURS: TCupColour[] = ['white', 'ice', 'black', 'blue', 'fuchsia', 'orange', 'red', 'green'];
const GLASS_COLOURS: TCupColour[] = ['white', 'ice', 'black'];

function zone(width: number, height: number, margins: { bottom: number; top: number }): IMarkingZone {
    return { height, marginBottom: margins.bottom, marginTop: margins.top, width };
}

const GOBELETCUP_CATALOG: ICatalogReference[] = [
    {
        name: 'ECO30',
        label: '2/3 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO30.png',
        surface: { width: 100.3, height: 45 },
        serigraphy: { ...zone(85.3, 25, { top: 10, bottom: 10 }), marginSide: 15, maxColours: 1 },
        quadriShort: { ...zone(100.3, 25, { top: 10, bottom: 10 }), placements: { centre: { marginTop: 10, marginBottom: 10 } } },
        quadriTall: { digital: null, iml: zone(100.3, 45, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO50',
        label: '4/5 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO50.png',
        surface: { width: 112.2, height: 59 },
        serigraphy: { ...zone(97.2, 39, { top: 10, bottom: 10 }), marginSide: 15, maxColours: 1 },
        quadriShort: { ...zone(112.2, 39, { top: 10, bottom: 10 }), placements: { centre: { marginTop: 10, marginBottom: 10 } } },
        quadriTall: { digital: null, iml: zone(112.2, 59, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO120',
        label: '10/12 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO120.png',
        surface: { width: 162.3, height: 71 },
        serigraphy: { ...zone(147.3, 51, { top: 10, bottom: 10 }), marginSide: 15, maxColours: 2 },
        quadriShort: { ...zone(162.3, 51, { top: 10, bottom: 10 }), placements: { centre: { marginTop: 10, marginBottom: 10 } } },
        quadriTall: { digital: null, iml: zone(162.3, 71, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO180',
        label: '12/18 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO180.png',
        surface: { width: 162.3, height: 96 },
        serigraphy: { ...zone(147.3, 71, { top: 10, bottom: 15 }), marginSide: 15, maxColours: 2 },
        quadriShort: { ...zone(162.3, 70, { top: 13, bottom: 13 }), placements: { centre: { marginTop: 13, marginBottom: 13 } } },
        quadriTall: { digital: zone(162.3, 76, { top: 10, bottom: 10 }), iml: zone(162.3, 96, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO200',
        label: '20/28 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO200.png',
        surface: { width: 200.5, height: 103 },
        serigraphy: { ...zone(185.5, 78, { top: 10, bottom: 15 }), marginSide: 15, maxColours: 2 },
        quadriShort: { ...zone(200.5, 70, { top: 10, bottom: 23 }), placements: { centre: { marginTop: 10, marginBottom: 23 } } },
        quadriTall: { digital: zone(200.5, 83, { top: 10, bottom: 10 }), iml: zone(200.5, 103, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO300',
        label: '25/33 cl',
        family: 'cup',
        cartonSize: 50,
        colours: [...ALL_CUP_COLOURS, 'yellow'],
        hasIml: true,
        imageUrl: '/catalog/ECO300.png',
        surface: { width: 200.5, height: 115 },
        serigraphy: { ...zone(185.5, 90, { top: 10, bottom: 15 }), marginSide: 15, maxColours: 2 },
        quadriShort: { ...zone(200.5, 70, { top: 21, bottom: 24 }), placements: { centre: { marginTop: 21, marginBottom: 24 } } },
        quadriTall: { digital: zone(200.5, 95, { top: 10, bottom: 10 }), iml: zone(200.5, 115, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO400',
        label: '33/40 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO400.png',
        surface: { width: 198.33, height: 138 },
        serigraphy: { ...zone(181.33, 113, { top: 10, bottom: 15 }), marginSide: 17, maxColours: 2 },
        quadriShort: {
            ...zone(198.33, 70, { top: 34, bottom: 34 }),
            placements: {
                centre: { marginTop: 34, marginBottom: 34 },
                top: { marginTop: 12, marginBottom: 56 },
                bottom: { marginTop: 58, marginBottom: 10 },
            },
        },
        quadriTall: { digital: zone(198.33, 118, { top: 10, bottom: 10 }), iml: zone(198.33, 138, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO450',
        label: '45/50 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO450.png',
        surface: { width: 226.5, height: 140 },
        serigraphy: { ...zone(206.5, 115, { top: 10, bottom: 15 }), marginSide: 20, maxColours: 2 },
        quadriShort: {
            ...zone(226.5, 70, { top: 35, bottom: 35 }),
            placements: {
                centre: { marginTop: 35, marginBottom: 35 },
                top: { marginTop: 11, marginBottom: 59 },
                bottom: { marginTop: 59, marginBottom: 11 },
            },
        },
        quadriTall: { digital: zone(226.5, 120, { top: 10, bottom: 10 }), iml: zone(226.5, 140, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO500',
        label: '50/56 cl',
        family: 'cup',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: true,
        imageUrl: '/catalog/ECO500.png',
        surface: { width: 226.5, height: 153 },
        serigraphy: { ...zone(206.5, 128, { top: 10, bottom: 15 }), marginSide: 20, maxColours: 2 },
        quadriShort: {
            ...zone(226.5, 70, { top: 41.5, bottom: 41.5 }),
            placements: {
                centre: { marginTop: 41.5, marginBottom: 41.5 },
                top: { marginTop: 14, marginBottom: 69 },
                bottom: { marginTop: 73, marginBottom: 10 },
            },
        },
        quadriTall: { digital: zone(226.5, 133, { top: 10, bottom: 10 }), iml: zone(226.5, 153, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECO900',
        label: '90/100 cl',
        family: 'cup',
        cartonSize: 175,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECO900.png',
        surface: { width: 358, height: 140 },
        serigraphy: { ...zone(333, 115, { top: 10, bottom: 15 }), marginSide: 25, maxColours: 2 },
        // PROVISIONAL margins of the client's table of 2026-10-26: they do not fit the 140 mm wall (144 + 70 + 144 =
        // 358, the wall's width), the client sends the definitive ones about two weeks later. They are kept
        // as given, and bandTopInMm keeps the band on the cup whatever they say.
        quadriShort: {
            ...zone(358, 70, { top: 144, bottom: 144 }),
            placements: {
                centre: { marginTop: 144, marginBottom: 144 },
                top: { marginTop: 10, marginBottom: 278 },
                bottom: { marginTop: 278, marginBottom: 10 },
            },
        },
        quadriTall: { digital: zone(358, 120, { top: 10, bottom: 10 }), iml: zone(358, 140, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECOTUBO25',
        label: '20/25 cl',
        family: 'cocktail',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECOTUBO25.png',
        surface: { width: 156, height: 144 },
        serigraphy: { ...zone(141, 119, { top: 10, bottom: 15 }), marginSide: 15, maxColours: 2 },
        quadriShort: {
            ...zone(156, 70, { top: 37, bottom: 37 }),
            placements: {
                centre: { marginTop: 37, marginBottom: 37 },
                top: { marginTop: 10, marginBottom: 64 },
                bottom: { marginTop: 63, marginBottom: 11 },
            },
        },
        quadriTall: { digital: zone(156, 124, { top: 10, bottom: 10 }), iml: zone(156, 144, { top: 0, bottom: 0 }) },
    },
    {
        name: 'ECOGRANITI35',
        label: '30/35 cl',
        family: 'cocktail',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECOGRANITI35.png',
        surface: { width: 240, height: 112 },
        serigraphy: { ...zone(225, 34, { top: 6, bottom: 72 }), marginSide: 15, maxColours: 2 },
        quadriShort: { ...zone(240, 29, { top: 6, bottom: 77 }), placements: { centre: { marginTop: 6, marginBottom: 77 } } },
        quadriTall: null,
    },
    {
        name: 'ECOGRANITI40',
        label: '33/40 cl',
        family: 'cocktail',
        cartonSize: 50,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECOGRANITI40.png',
        surface: { width: 237.5, height: 124 },
        serigraphy: { ...zone(222.5, 48, { top: 11, bottom: 65 }), marginSide: 15, maxColours: 2 },
        quadriShort: { ...zone(237.5, 40, { top: 11, bottom: 73 }), placements: { centre: { marginTop: 11, marginBottom: 73 } } },
        quadriTall: null,
    },
    {
        name: 'ECOCHOPE',
        label: '50/56 cl',
        family: 'cocktail',
        cartonSize: 60,
        colours: ALL_CUP_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECOCHOPE.png',
        surface: { width: 224.624, height: 163 },
        serigraphy: { ...zone(156.624, 100, { top: 10, bottom: 53 }), marginSide: 34, maxColours: 2 },
        quadriShort: null,
        quadriTall: null,
    },
    {
        name: 'ECOVIN',
        label: '12/18 cl',
        family: 'wine',
        cartonSize: 50,
        colours: GLASS_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECOVIN.png',
        surface: { width: 178.5, height: 150 },
        serigraphy: { ...zone(163.5, 55, { top: 10, bottom: 85 }), marginSide: 15, maxColours: 1 },
        quadriShort: { ...zone(178.5, 55, { top: 10, bottom: 85 }), placements: { centre: { marginTop: 10, marginBottom: 85 } } },
        quadriTall: null,
    },
    {
        name: 'ECOVIN25',
        label: '25/30 cl',
        family: 'wine',
        cartonSize: 66,
        colours: GLASS_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECOVIN25.png',
        surface: { width: 244, height: 170 },
        serigraphy: { ...zone(229, 40, { top: 10, bottom: 120 }), marginSide: 15, maxColours: 1 },
        quadriShort: { ...zone(244, 40, { top: 10, bottom: 120 }), placements: { centre: { marginTop: 10, marginBottom: 120 } } },
        quadriTall: null,
    },
    {
        name: 'ECOCHAMP',
        label: '12/15 cl',
        family: 'wine',
        cartonSize: 50,
        colours: GLASS_COLOURS,
        hasIml: false,
        imageUrl: '/catalog/ECOCHAMP.png',
        surface: { width: 172.8, height: 180 },
        serigraphy: { ...zone(157.8, 40, { top: 10, bottom: 130 }), marginSide: 15, maxColours: 1 },
        quadriShort: { ...zone(172.8, 40, { top: 10, bottom: 130 }), placements: { centre: { marginTop: 10, marginBottom: 130 } } },
        quadriTall: null,
    },
    {
        // Two zones on opposite faces (the technical sheet, p. 19): the same 80 x 90 mm zone
        // twice, recto and verso. Offered as recto only or recto-verso.
        name: 'ECOJAR100',
        label: '1 L',
        family: 'pitcher',
        cartonSize: 66,
        colours: ['ice'],
        hasIml: false,
        hasVerso: true,
        imageUrl: '/catalog/ECOJAR100.png',
        surface: { width: 330, height: 167 },
        serigraphy: { ...zone(80, 90, { top: 52, bottom: 25 }), marginSide: 85, maxColours: 2 },
        quadriShort: null,
        quadriTall: null,
    },
    {
        name: 'ECOJAR150',
        label: '1,5 L',
        family: 'pitcher',
        cartonSize: 60,
        colours: ['ice'],
        hasIml: false,
        hasVerso: true,
        imageUrl: '/catalog/ECOJAR150.png',
        surface: { width: 375, height: 202 },
        serigraphy: { ...zone(85, 120, { top: 57, bottom: 25 }), marginSide: 102.5, maxColours: 2 },
        quadriShort: null,
        quadriTall: null,
    },
];

function getCatalogReference(name: string): ICatalogReference | undefined {
    return GOBELETCUP_CATALOG.find((reference) => reference.name === name);
}

// The client's renders exist in every cup colour, named with their own suffixes
// (`gab1_ECO300 BL.png` is the blue one); frosted is the unsuffixed base render.
const CUP_COLOUR_FILE_SUFFIX: Record<TCupColour, string> = {
    black: 'N',
    blue: 'BL',
    fuchsia: 'F',
    green: 'V',
    ice: '',
    orange: 'O',
    red: 'R',
    white: 'BA',
    yellow: 'J',
};

// What each colour looks like to the customer. Distinct from CUP_COLOUR_HEX in
// useConfigurationActions, which is what the canvas is painted with and turns frosted into
// transparency: here frosted is the pale tint the eye actually sees, which is what a swatch
// and a screen-printed light end both need.
const CUP_COLOUR_SWATCH: Record<TCupColour, string> = {
    black: '#1F1F1F',
    blue: '#2B7BD3',
    fuchsia: '#D3167E',
    green: '#7BC043',
    ice: '#E6EEEF',
    orange: '#F28C28',
    red: '#D93025',
    white: '#FFFFFF',
    yellow: '#F5C400',
};

function cupImageUrl(reference: ICatalogReference, colour: TCupColour | undefined): string {
    if (!colour || !reference.colours.includes(colour) || CUP_COLOUR_FILE_SUFFIX[colour] === '') {
        return reference.imageUrl;
    }
    return `/catalog/${reference.name}-${CUP_COLOUR_FILE_SUFFIX[colour]}.png`;
}

export { CUP_COLOUR_SWATCH, cupImageUrl, getCatalogReference, GOBELETCUP_CATALOG };

export type { ICatalogReference, IMarkingZone, TCatalogFamily, TCupColour, TQuadriPlacement };
